import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../config/firebase';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';

const DevSetup = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const addResult = (message, type = 'info') => {
    setResults(prev => [...prev, { message, type, timestamp: new Date().toLocaleTimeString() }]);
  };

  const handleDevSetup = async () => {
    try {
      setLoading(true);
      setResults([]);
      
      addResult('🚀 Starting dev environment setup...', 'info');

      // Step 1: Set super admin custom claims
      try {
        addResult('📝 Setting super admin custom claims...', 'info');
        const setSuperAdmin = httpsCallable(functions, 'setSuperAdmin');
        const response = await setSuperAdmin();
        addResult(`✅ Custom claims set: ${response.data.message}`, 'success');
      } catch (error) {
        addResult(`❌ Failed to set custom claims: ${error.message}`, 'error');
      }

      // Step 2: Check if tenants exist
      addResult('🔍 Checking existing tenants...', 'info');
      const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
      const existingTenants = tenantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (existingTenants.length === 0) {
        addResult('📋 No tenants found, creating default tenant...', 'info');
        
        // Create default tenant
        const defaultTenant = {
          id: 'healehub-default',
          name: 'HealeHub Default',
          slug: 'healehub-default',
          domain: 'healehub-dev.web.app',
          status: 'active',
          createdAt: new Date().toISOString(),
          settings: {
            branding: {
              primaryColor: '#2563eb',
              logo: null
            },
            features: {
              multiTenant: true,
              emailNotifications: true
            }
          }
        };

        await setDoc(doc(db, 'tenants', defaultTenant.id), defaultTenant);
        addResult(`✅ Created default tenant: ${defaultTenant.name}`, 'success');
      } else {
        addResult(`✅ Found ${existingTenants.length} existing tenants`, 'success');
        existingTenants.forEach(tenant => {
          addResult(`   • ${tenant.name} (${tenant.id})`, 'info');
        });
      }

      // Step 3: Update user profile with tenant assignment
      addResult('👤 Updating user profile with tenant assignment...', 'info');
      const userProfile = {
        uid: 'christheale-dev-uid', // This should be the actual Firebase Auth UID
        email: 'christheale@gmail.com',
        firstName: 'Chris',
        lastName: 'Heale',
        role: 'super_admin',
        tenantId: existingTenants.length > 0 ? existingTenants[0].id : 'healehub-default',
        permissions: ['admin_access', 'super_admin_access', 'tenant_management'],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Note: We can't get the actual UID without auth, so this is a placeholder
      addResult('⚠️ User profile update requires actual Firebase Auth UID', 'warning');
      addResult('   Please set tenantId manually in Firebase Console if needed', 'warning');

      // Step 4: Create clinic subcollections structure
      const tenantId = existingTenants.length > 0 ? existingTenants[0].id : 'healehub-default';
      addResult(`🏥 Setting up clinic structure for tenant: ${tenantId}...`, 'info');

      // Create empty documents in subcollections to establish structure
      const collections = ['clients', 'appointments', 'staff', 'rooms', 'invoices', 'notes', 'settings', 'waitlist', 'activities'];
      
      for (const collectionName of collections) {
        try {
          // Create a placeholder document to establish the collection
          await setDoc(doc(db, 'clinics', tenantId, collectionName, '_placeholder'), {
            _placeholder: true,
            createdAt: new Date().toISOString(),
            note: 'This document establishes the collection structure and can be deleted once real data is added'
          });
          addResult(`   ✅ Created ${collectionName} collection`, 'success');
        } catch (error) {
          addResult(`   ❌ Failed to create ${collectionName}: ${error.message}`, 'error');
        }
      }

      addResult('🎉 Dev setup complete! Please refresh the page.', 'success');
      
      setTimeout(() => {
        toast.success('Setup complete! Please refresh the page to see changes.');
      }, 1000);

    } catch (error) {
      console.error('Dev setup error:', error);
      addResult(`❌ Setup failed: ${error.message}`, 'error');
      toast.error(`Setup failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              🚀 HealeHub Dev Environment Setup
            </h1>
            <p className="text-gray-600 mb-6">
              This will configure the dev environment with proper permissions and tenant structure.
            </p>
            
            <button
              onClick={handleDevSetup}
              disabled={loading}
              className={`w-full py-3 px-6 rounded-lg font-medium transition-colors mb-4 ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? '⚙️ Setting up...' : '🚀 Setup Dev Environment'}
            </button>
            
            <button
              onClick={async () => {
                try {
                  addResult('🔧 Setting super admin claims for chris@healehub.com...', 'info');
                  const setSuperAdminFunction = httpsCallable(functions, 'setSuperAdmin');
                  const response = await setSuperAdminFunction();
                  addResult('✅ Super admin claims set successfully!', 'success');
                  addResult('🔄 Please refresh the page to see changes...', 'info');
                  setTimeout(() => window.location.reload(), 2000);
                } catch (error) {
                  addResult(`❌ Error setting super admin claims: ${error.message}`, 'error');
                }
              }}
              className="w-full py-3 px-6 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              🔧 Set Super Admin Claims
            </button>
          </div>

          {results.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Setup Progress:</h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className={`mb-2 text-sm ${
                      result.type === 'success' ? 'text-green-600' :
                      result.type === 'error' ? 'text-red-600' :
                      result.type === 'warning' ? 'text-orange-600' :
                      'text-gray-600'
                    }`}
                  >
                    <span className="text-gray-400 mr-2">[{result.timestamp}]</span>
                    {result.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">What this setup does:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Sets Firebase Auth custom claims for super admin access</li>
              <li>• Creates default tenant if none exist</li>
              <li>• Sets up multi-tenant clinic structure (clinics/tenantId/collections)</li>
              <li>• Establishes all required subcollections with placeholder docs</li>
              <li>• Configures proper permissions for the dev environment</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevSetup;
